import {Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {concatLatestFrom} from '@ngrx/operators';
import {select, Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';
import {ContextData} from '../../common/context-data';
import * as contextSelectors from '../../common/context-data/context-data.selectors';
import * as eventActions from '../../common/event-emitter/event.actions';
import {filterNullOrUndefined} from '../../common/service';
import * as cartActions from '../../common/shopping-cart/cart.actions';
import {
  CartItem,
  Image,
  Persona,
  ProductComponentModel,
  ServiceOffer,
} from '../../data/model';
import {
  MarketingInfoService,
  MarketingSignupPayload,
} from '../../data/api/service/marketing-info.service';
import * as addonSelectors from '../reducers';

export interface ServiceOfferState {
  formIsValid: boolean;
}

export interface ServiceOfferViewModel {
  hasImage: boolean;
  image: Image;
  headline: string;
  tagline: string;
  offer: ServiceOffer;
  showQuoteSpinner: boolean;
  showQuote: boolean;
  showPrice: boolean;
  formToDisplay: string;
  description: string;
  formIsValid: boolean;
  perils: string[];
  contextData?: ContextData;
}

function replaceParticipantFullName(
  source: string,
  participant?: Persona,
): string {
  return source.replaceAll(
    /(\${participantFullName})/g,
    `${participant?.firstName} ${participant?.lastName}`,
  );
}

function replaceEventType(source: string, event?: string | null): string {
  return source.replaceAll(/(\${eventType})/g, event ?? '');
}

function replaceFirstName(source: string, name?: string | null): string {
  return source.replaceAll(/\${firstName}/g, name ?? '');
}

function replaceBossOrganizationName(
  source: string,
  orgName?: string | null,
): string {
  return source.replaceAll(/\${bossOrganizationName}/g, orgName ?? '');
}

const MN_WILD_CATALOG_ID = 'minnesota-wild-catalog';
const SPORTSENGINE_PLAY_CATALOG_ID = 'sportsengine-play-catalog';

@Injectable()
export class ServiceOfferStore extends ComponentStore<ServiceOfferState> {
  readonly productComponentModel$: Observable<
    ProductComponentModel<ServiceOffer>
  > = this.select(
    this.store.pipe(
      select(addonSelectors.selectAddonByCatalogId),
      filterNullOrUndefined(),
      map(addon => <ProductComponentModel<ServiceOffer>>addon),
    ),
    (addon: ProductComponentModel<ServiceOffer>) => addon,
  );

  readonly contextData$: Observable<ContextData | undefined> = this.select(
    this.store.select(contextSelectors.selectContextData),
    (contextData: ContextData | undefined) => contextData,
  );

  readonly cartItem$: Observable<CartItem | undefined> = this.select(
    this.store.pipe(select(addonSelectors.selectCurrentAddOnFromCart)),
    (cartItem: CartItem | undefined) => cartItem,
  );

  readonly offer$: Observable<ServiceOffer> = this.select(
    this.productComponentModel$,
    (product: ProductComponentModel<ServiceOffer>) => product.data,
  );

  readonly formToDisplay$: Observable<string> = this.select(
    this.productComponentModel$,
    (product: ProductComponentModel<ServiceOffer>) => {
      let result = 'checkbox';
      if (product.data.inputLabels?.length === 2) {
        result = 'radio';
      }
      if (product.catalogId === MN_WILD_CATALOG_ID) {
        result = 'select';
      }
      if (product.catalogId === SPORTSENGINE_PLAY_CATALOG_ID) {
        result = 'cart-button';
      }
      return result;
    },
  );

  readonly showQuote$: Observable<boolean> = this.select(
    this.productComponentModel$,
    (product: ProductComponentModel<ServiceOffer>) =>
      product.data.quoteRequired() &&
      !!product.data.quote &&
      product.data.quote.cost > 0,
  );

  readonly showPrice$: Observable<boolean> = this.select(
    this.productComponentModel$,
    this.showQuote$,
    (product: ProductComponentModel<ServiceOffer>, showQuote: boolean) =>
      !showQuote && product.data.price > 0,
  );

  readonly hasImage$: Observable<boolean> = this.select(
    this.offer$,
    (offer: ServiceOffer) => offer.images.length > 0,
  );

  readonly firstImage$: Observable<Image> = this.select(
    this.productComponentModel$,
    (product: ProductComponentModel<ServiceOffer>) => product.data.images[0],
  );

  readonly quoteIsLoading$: Observable<boolean> = this.select(
    this.productComponentModel$,
    (product: ProductComponentModel<ServiceOffer>) =>
      !!product.data.quoteProvider && !product.data.quote,
  );

  readonly headlineText$: Observable<string> = this.select(
    this.productComponentModel$,
    this.contextData$,
    (
      product: ProductComponentModel<ServiceOffer>,
      context: ContextData | undefined,
    ) => {
      const signup = context?.signups[0];
      let result =
        product.data.quote?.displayOverrides?.headline ||
        product.data.headline ||
        '';
      result = replaceEventType(result, signup?.requestDetails.sport);
      result = replaceFirstName(result, signup?.participant.firstName);
      result = replaceParticipantFullName(result, signup?.participant);
      return result;
    },
  );

  readonly descriptionText$: Observable<string> = this.select(
    this.productComponentModel$,
    this.contextData$,
    (
      product: ProductComponentModel<ServiceOffer>,
      context: ContextData | undefined,
    ) => {
      const description =
        product.data.quote?.displayOverrides?.promotional_description ||
        product.data.description ||
        '';
      const signup = context?.signups[0];
      let result = replaceBossOrganizationName(
        description,
        context?.bossOrganization.name,
      );
      result = replaceParticipantFullName(result, signup?.participant);
      return result;
    },
  );

  readonly tagLineText$: Observable<string> = this.select(
    this.productComponentModel$,
    this.contextData$,
    (
      product: ProductComponentModel<ServiceOffer>,
      context: ContextData | undefined,
    ) => {
      let result = product?.data.tagline || '';
      if (
        product?.data.tagline?.includes('${bossOrganizationName}') &&
        context
      ) {
        const eventTypeRegex = /(\${bossOrganizationName})+/g;
        result = product.data.tagline.replace(
          eventTypeRegex,
          context.bossOrganization.name,
        );
      }
      return result;
    },
  );

  readonly formIsValid$: Observable<boolean> = this.select(
    state => state.formIsValid,
  );

  readonly perils$: Observable<string[]> = this.select(
    this.productComponentModel$,
    (product: ProductComponentModel<ServiceOffer>) =>
      product.data.quote?.displayOverrides?.perils || [],
  );

  readonly vm$: Observable<ServiceOfferViewModel> = this.select(
    this.hasImage$,
    this.firstImage$,
    this.headlineText$,
    this.tagLineText$,
    this.offer$,
    this.quoteIsLoading$,
    this.showQuote$,
    this.showPrice$,
    this.formToDisplay$,
    this.descriptionText$,
    this.formIsValid$,
    this.perils$,
    this.contextData$,
    (
      hasImage: boolean,
      image: Image,
      headline: string,
      tagline: string,
      offer: ServiceOffer,
      showQuoteSpinner: boolean,
      showQuote: boolean,
      showPrice: boolean,
      formToDisplay: string,
      description: string,
      formIsValid: boolean,
      perils: string[],
      contextData: ContextData | undefined,
    ): ServiceOfferViewModel => ({
      hasImage,
      image,
      headline,
      tagline,
      offer,
      showQuoteSpinner,
      showQuote,
      showPrice,
      formToDisplay,
      description,
      formIsValid,
      perils,
      contextData,
    }),
  );

  readonly setFormValidity = this.updater(
    (state: ServiceOfferState, formIsValid: boolean): ServiceOfferState => ({
      ...state,
      formIsValid,
    }),
  );

  readonly removeItemFromCart = this.effect((cartItem$: Observable<CartItem>) =>
    cartItem$.pipe(
      tap((cartItem: CartItem) =>
        this.store.dispatch(cartActions.removeItemFromCart({item: cartItem})),
      ),
    ),
  );

  readonly addItemToCart = this.effect((cartItem$: Observable<CartItem>) =>
    cartItem$.pipe(
      tap((cartItem: CartItem) =>
        this.store.dispatch(cartActions.addOrUpdateCartItem({item: cartItem})),
      ),
    ),
  );

  readonly updateCartItem = this.effect((cartItem$: Observable<CartItem>) =>
    cartItem$.pipe(
      tap((cartItem: CartItem) =>
        this.store.dispatch(cartActions.updateCartItem({item: cartItem})),
      ),
    ),
  );

  readonly continue = this.effect(
    (args$: Observable<{selected: boolean; item: CartItem}>) =>
      args$.pipe(
        concatLatestFrom(() => [this.cartItem$, this.productComponentModel$]),
        tap(([args, cartItem, productComponentModel]) => {
          if (
            args.selected &&
            productComponentModel.catalogId === MN_WILD_CATALOG_ID &&
            cartItem &&
            cartItem.unitPrice !== args.item.unitPrice
          ) {
            const updated = new CartItem({
              ...cartItem,
              unitPrice: args.item.unitPrice,
            });
            this.updateCartItem(updated);
          } else if (
            args.selected &&
            (!cartItem || !args.item.equals(cartItem))
          ) {
            this.addItemToCart(args.item);
          } else if (!args.selected && cartItem) {
            this.removeItemFromCart(cartItem);
          }
          this.sendMarketingSignupIfOptedIn(args.item);
        }),
      ),
  );

  readonly complete = this.effect(
    (args$: Observable<{selected: boolean; item: CartItem}>) =>
      args$.pipe(
        concatLatestFrom(() => this.cartItem$),
        tap(([args, cartItem]) => {
          if (
            args.selected &&
            args.item &&
            (!cartItem || !args.item.equals(cartItem))
          ) {
            this.store.dispatch(
              cartActions.addItemToCartAndComplete({item: args.item}),
            );
          } else if (!args.selected && cartItem) {
            this.store.dispatch(
              cartActions.removeItemFromCartThenComplete({item: cartItem}),
            );
          } else {
            this.store.dispatch(cartActions.completeOrder());
          }
          this.sendMarketingSignupIfOptedIn(args.item);
        }),
      ),
  );

  readonly registerAnotherPerson = this.effect(
    (args$: Observable<{selected: boolean; item: CartItem}>) =>
      args$.pipe(
        concatLatestFrom(() => this.cartItem$),
        tap(([args, cartItem]) => {
          if (args.selected && (!cartItem || !args.item.equals(cartItem))) {
            this.store.dispatch(
              cartActions.addItemToCartAndRegisterAnother({
                item: args.item,
              }),
            );
          } else if (!args.selected && cartItem) {
            this.store.dispatch(
              cartActions.removeItemFromCartThenRegisterAnother({
                item: cartItem,
              }),
            );
          } else {
            this.store.dispatch(eventActions.registerAnother());
          }
          this.sendMarketingSignupIfOptedIn(args.item);
        }),
      ),
  );

  readonly sendMarketingSignupIfOptedIn = this.effect(
    (item$: Observable<CartItem>) =>
      item$.pipe(
        filter((item: CartItem) => !!item?.marketingOptIn),
        concatLatestFrom(() => [this.offer$, this.contextData$]),
        tap(([, offer, contextData]) => {
          if (!offer.marketingUrl) {
            return;
          }
          const registrant = contextData?.registrant;
          if (!registrant?.email) {
            return;
          }
          const payload: MarketingSignupPayload = {
            userId: registrant.email,
            event: 'sportsengine_leadcapture',
            properties: {
              email: registrant.email,
              first_name: registrant.firstName ?? '',
              last_name: registrant.lastName ?? '',
              source: 'sportsengine',
              unsubscribed: false,
            },
          };
          if (registrant.address?.state) {
            payload.properties.state = registrant.address.state;
          }
          this.marketingInfoService.sendMarketingSignup(payload).subscribe();
        }),
      ),
  );

  constructor(
    private store: Store,
    private marketingInfoService: MarketingInfoService,
  ) {
    super({
      formIsValid: false,
    });
  }
}
